import type { AlfaPlatform } from './alfaApp';

/**
 * P2P-3116: «Скачать QR» на странице alfa_page. Картинка 1080×1080: сумма, номер заказа и QR.
 * Сканер Альфы показывает картинку из галереи во всю ширину, а рамка распознавания занимает
 * примерно центр по горизонтали и нижние ~70% по вертикали, поэтому QR - 46% ширины, чуть ниже
 * центра (по прототипу из ТЗ).
 */
export const QR_PNG_SIZE = 1080;
const QR_WIDTH_SHARE = 0.46;
const QR_CENTER_Y_SHARE = 0.6;
const FONT = "'Lato', 'Helvetica Neue', system-ui, -apple-system, sans-serif";

/** Адрес картинки QR, нарисованного в контейнере: <img> как есть, <svg> react-qr-code - data URL. */
export const getQrImageSrc = (container: HTMLElement | null): string | null => {
  const img = container?.querySelector('img');

  if (img?.src) {
    return img.src;
  }

  const svg = container?.querySelector('svg');

  if (!svg) {
    return null;
  }

  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(clone))}`;
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    // Картинка провайдера (P2P-3043) с другого домена: без CORS холст не отдаст PNG - тогда
    // сохранить не получится, и плательщик увидит подсказку сделать снимок экрана.
    if (!src.startsWith('data:')) {
      image.crossOrigin = 'anonymous';
    }
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('QR image is not loaded'));
    image.src = src;
  });

type RenderQrPngParams = {
  qrSrc: string;
  amount: string;
  orderNumber: string;
};

export const renderQrPng = async ({
  qrSrc,
  amount,
  orderNumber,
}: RenderQrPngParams): Promise<Blob | null> => {
  const qr = await loadImage(qrSrc);
  const canvas = document.createElement('canvas');
  canvas.width = QR_PNG_SIZE;
  canvas.height = QR_PNG_SIZE;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  const qrSize = Math.round(QR_PNG_SIZE * QR_WIDTH_SHARE);
  const qrX = Math.round((QR_PNG_SIZE - qrSize) / 2);
  const qrY = Math.round(QR_PNG_SIZE * QR_CENTER_Y_SHARE - qrSize / 2);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, QR_PNG_SIZE, QR_PNG_SIZE);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(qr, qrX, qrY, qrSize, qrSize);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#1b1b1f';
  ctx.font = `600 40px ${FONT}`;
  ctx.fillText('К оплате', QR_PNG_SIZE / 2, 140);
  ctx.font = `700 92px ${FONT}`;
  ctx.fillText(amount, QR_PNG_SIZE / 2, 240);
  ctx.fillStyle = '#6b6b76';
  ctx.font = `400 36px ${FONT}`;
  ctx.fillText(`Заказ #${orderNumber}`, QR_PNG_SIZE / 2, 300);

  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    } catch {
      // Холст «испачкан» картинкой с чужого домена.
      resolve(null);
    }
  });
};

const downloadFile = (file: File) => {
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

export type SaveQrResult = 'shared' | 'downloaded' | 'cancelled';

/**
 * iOS: обычное скачивание кладёт картинку в «Файлы», а сканер Альфы открывает «Фото», поэтому
 * открываем меню «Поделиться» (в нём «Сохранить изображение»). Нет navigator.share - скачивание.
 * Android и компьютер: скачивание PNG (на Android файл виден в галерее, в «Загрузках»).
 */
export const saveQrFile = async (
  file: File,
  platform: AlfaPlatform | null,
): Promise<SaveQrResult> => {
  if (platform === 'ios' && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });

      return 'shared';
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') {
        return 'cancelled';
      }
    }
  }

  downloadFile(file);

  return 'downloaded';
};
